import { AfterViewInit, Component, ElementRef, HostBinding, Input } from '@angular/core';
import { appEnv } from '../../utils/app_env';

// Avatar frame: env-colored ring + ADMIN pill (users.is_admin only). Wrap the avatar's img/initials (or the
// .symbol holding them) in it. Base styles are global (.avatar-wrapper / .admin-badge in styles.scss).
//   size              ring/pill scale: 'xs' | 'sm' | 'lg' | 'xl' (xl: large profile avatars, ~125px)
//   fit               ring and pill drawn inside the avatar's own box — same size as a bare avatar (lists)
//   centered          shift the frame up so ring + hanging pill are centered where the bare avatar was (navbar)
//   plainWhenNotAdmin no ring for non-admins (by default every user gets the ring, only admins the pill)
//   autoLoadingCircle detect a loading app-image/<img> and show the loading circle (off where the page manages it)
// Loading circle: any wrapped element with class .admin-avatar-loading (e.g. a placeholder the page renders itself)
// Photo fade-in: any wrapped element with class .admin-avatar-fade-in (auto for loading <img>s and app-image photos)
@Component({
    selector: 'app-admin-avatar',
    templateUrl: './admin-avatar.component.html',
    styles: [`
        :host { display: inline-flex; vertical-align: middle; }
        /* Loading circle: a pulsing circle in the frame's colors (env gradient for admins, the initials' default
           gradient otherwise) in the photo's own box, so nothing shifts when it arrives. Two cases:
           - app-image (lists, details) shows picture-icon.png until the photo arrives — matched by its src
             (app-image's imagePlaceholderURL); object-position pushes the icon out of the box so only the
             background shows; overrides app-image's blink/blur
           - a plain <img> (navbar, menu) is empty while loading — ngAfterViewInit marks it until load/error
           - any element the page marks with .admin-avatar-loading itself (login: until it knows the photo)
           Hidden for the first 150ms, so photos coming from the browser cache (still decoding) don't flash it,
           then eases in before pulsing.
           Only reaches avatars inside this component */
        :host(:not(.admin-avatar--no-auto-loading-circle)) ::ng-deep img[src$="picture-icon.png"],
        :host ::ng-deep .admin-avatar-loading {
            object-position: -9999px 0;
            background: var(--admin-accent-gradient, linear-gradient(135deg, #7d27b8, #cc1a78));
            border-radius: 50%;
            filter: none;
            opacity: 0;
            animation: adminAvatarLoadingIn 0.3s ease-out 150ms both, adminAvatarLoadingPulse 1s ease-in-out 450ms infinite alternate;
        }
        @keyframes adminAvatarLoadingIn {
            from { opacity: 0; }
            to { opacity: 0.35; }
        }
        @keyframes adminAvatarLoadingPulse {
            from { opacity: 0.35; }
            to { opacity: 0.7; }
        }
        /* Photo/initials fade-in, the same quick one everywhere: plain <img>s that finished loading
           (ngAfterViewInit), app-image's photo (its own 0.5s .fade-in, made the same here — not its placeholder),
           initials (e.g. app-image falling back to them when a photo is missing) and any element the page marks
           with .admin-avatar-fade-in (login). Starts at about the loading circle's opacity, so circle → photo or
           initials reads as one smooth change instead of dipping to nothing. Not for what's already there when
           the avatar renders (nothing was loading) — ngAfterViewInit marks those .admin-avatar-no-fade */
        :host ::ng-deep .admin-avatar-fade-in:not(.admin-avatar-no-fade),
        :host ::ng-deep img.fade-in:not([src$="picture-icon.png"]):not(.admin-avatar-no-fade),
        :host ::ng-deep .user-avatar-initials:not(.admin-avatar-no-fade) {
            animation: adminAvatarFadeIn 0.3s ease-out;
        }
        @keyframes adminAvatarFadeIn {
            from { opacity: 0.3; }
            to { opacity: 1; }
        }
        /* The global ring is a full gradient disc behind the avatar; cut its center out so transparent
           photos and the loading placeholder don't show the gradient through. The band reaches 1px under
           the avatar's edge, otherwise its antialiased edge shows a dark hairline against the ring */
        .avatar-inner::before {
            -webkit-mask: radial-gradient(closest-side, transparent calc(100% - var(--ring-offset) - 1px), #000 calc(100% - var(--ring-offset) - 1px));
            mask: radial-gradient(closest-side, transparent calc(100% - var(--ring-offset) - 1px), #000 calc(100% - var(--ring-offset) - 1px));
        }
        /* Env colors for avatars outside the navbar/login page (which set them on an ancestor) — same values as
           .navbar-env-border--* in styles.scss, keep in sync; production keeps the default gradient like the navbar */
        .admin-avatar--local { --admin-accent-gradient: linear-gradient(135deg, #9b6ef3, #7239ea); }
        .admin-avatar--testing { --admin-accent-gradient: linear-gradient(135deg, #6ba3ff, #2979ff); }
        .admin-avatar--staging { --admin-accent-gradient: linear-gradient(135deg, #6ee7b7, #10b981); }
        /* xl: defined here, not with xs/sm/lg in styles.scss — only this component uses it */
        .avatar-wrapper--xl { --ring-offset: 4px; --badge-font-size: 11px; }
        .avatar-wrapper--xl .admin-badge { padding: 2px 8px; border-radius: 4px; letter-spacing: 0.6px; }
        /* centered: the ring adds --ring-offset above the avatar but the pill hangs half its height
           (font × 1.5 line-height + 2px padding) below — shift up by half the difference */
        .avatar-wrapper--centered.is-admin {
            transform: translateY(calc((var(--ring-offset) - var(--badge-font-size) * 0.75 - 1px) / 2));
        }
        /* fit: ring, avatar and the hanging pill all fit in the bare avatar's box (so a row with it lines up
           with rows without it). The ring sits on the avatar's edge with the avatar scaled down inside it
           (0.9: it slightly overlaps the 2px ring of a 32-35px avatar, so no gap shows between them); the whole
           circle shrinks to the top of the box (0.84) to leave room for the lower half of the pill, which hangs
           from the circle's bottom (16% up). Tuned for the 'sm' pill on 32-35px avatars. */
        .avatar-wrapper--fit .avatar-inner { transform: scale(0.84); transform-origin: top center; }
        .avatar-wrapper--fit .avatar-inner::before { inset: 0; }
        .avatar-wrapper--fit .avatar-inner ::ng-deep > * { transform: scale(0.9); }
        .avatar-wrapper--fit .admin-badge { bottom: 16%; }
    `],
})
export class AdminAvatarComponent implements AfterViewInit {
    @Input() isAdmin = false;
    @Input() size = 'sm'; // 'xs' | 'sm' | 'lg' | 'xl' (app-image passes its initialsSize string)
    @Input() fit = false;
    @Input() centered = false;
    @Input() plainWhenNotAdmin = false;
    @Input() autoLoadingCircle = true;

    @HostBinding('class.admin-avatar--no-auto-loading-circle') get noAutoLoadingCircle(): boolean {
        return !this.autoLoadingCircle;
    }

    // Same env colors as the navbar (which has none for production — it keeps the default gradient)
    readonly envClass = ['local', 'testing', 'staging'].includes(appEnv()) ? ' admin-avatar--' + appEnv() : '';

    get wrapperClass(): string {
        return 'avatar-wrapper avatar-wrapper--' + this.size
            + (this.isAdmin ? ' is-admin' : '')
            + (this.fit ? ' avatar-wrapper--fit' : '')
            + (this.centered ? ' avatar-wrapper--centered' : '')
            + this.envClass;
    }

    constructor(private elementRef: ElementRef<HTMLElement>) {}

    // Initials/photos already shown on render don't fade in (nothing was loading). Plain <img>s still loading get
    // the loading circle, then fade in once loaded. Classes set directly — no change detection involved; runs
    // before the first paint, so the skipped fade never shows
    ngAfterViewInit(): void {
        const host = this.elementRef.nativeElement;

        host.querySelectorAll('.user-avatar-initials').forEach(el => el.classList.add('admin-avatar-no-fade'));
        host.querySelectorAll('img').forEach(img => {
            if (img.complete && !img.src.endsWith('picture-icon.png')) img.classList.add('admin-avatar-no-fade');
        });

        if (!this.autoLoadingCircle) return;

        host.querySelectorAll('img').forEach(img => {
            if (img.complete) return;

            img.classList.add('admin-avatar-loading');
            img.addEventListener('load', () => img.classList.replace('admin-avatar-loading', 'admin-avatar-fade-in'), {once: true});
            img.addEventListener('error', () => img.classList.remove('admin-avatar-loading'), {once: true});
        });
    }
}
